import React, { useEffect, useState } from "react";
import { api, CHF } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";

const emptyGroup = { name: "", type: "radio", required: false, min: 0, max: 0, options: [] };

export default function AddonsTab() {
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | id
  const [form, setForm] = useState(emptyGroup);
  const [applyGroup, setApplyGroup] = useState(null);
  const [targets, setTargets] = useState([]);
  const [replace, setReplace] = useState(false);

  const load = () => api.get("/addon-groups").then((r) => setGroups(r.data));
  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); loadProducts(); }, []);

  const openNew = () => { setForm(emptyGroup); setEditing("new"); };
  const openEdit = (g) => { setForm({ ...g }); setEditing(g.id); };

  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, { name: "", price: 0 }] }));
  const updOpt = (oi, patch) => setForm((f) => ({ ...f, options: f.options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) }));
  const delOpt = (oi) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== oi) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom du groupe requis");
    if (form.options.length === 0) return toast.error("Ajoutez au moins une option");
    const payload = {
      name: form.name.trim(), type: form.type, required: !!form.required,
      min: parseInt(form.min) || 0, max: parseInt(form.max) || 0,
      options: form.options.map((o) => ({ ...o, name: o.name, price: parseFloat(o.price) || 0 })),
    };
    try {
      if (editing === "new") await api.post("/addon-groups", payload);
      else await api.put(`/addon-groups/${editing}`, payload);
      toast.success("Groupe d'addons enregistré");
      setEditing(null); load(); loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (g) => {
    if (!window.confirm(`Supprimer le groupe « ${g.name} » de la bibliothèque ?`)) return;
    await api.delete(`/addon-groups/${g.id}`);
    toast.success("Groupe supprimé"); load();
  };

  const openApply = (g) => { setApplyGroup(g); setTargets([]); setReplace(false); };
  const doApply = async () => {
    if (targets.length === 0) return toast.error("Sélectionnez au moins un produit");
    await api.post("/products/apply-addon-group", { group_id: applyGroup.id, product_ids: targets, replace });
    toast.success(`Appliqué à ${targets.length} produit(s)`);
    setApplyGroup(null); loadProducts();
  };

  const toggleTarget = (id) => setTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const cats = [...new Set(products.map((p) => p.category))];

  return (
    <div data-testid="addons-tab">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-display text-2xl font-bold">Espace addons ({groups.length})</h2>
        <Button onClick={openNew} className="rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="new-addon-group-btn"><Plus className="w-4 h-4 mr-2" />Nouveau groupe</Button>
      </div>
      <p className="text-sm text-white/50 mb-4">Créez ici vos groupes d'addons réutilisables (ex. « Choix du riz » avec Riz nature 0 CHF, Riz cantonais +2.5 CHF), puis appliquez-les en un clic à plusieurs produits.</p>

      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.id} className="border border-white/15 p-3" data-testid={`addon-group-${g.id}`}>
            <div className="flex items-center gap-3">
              <Layers className="w-5 h-5 text-brand-amber shrink-0" />
              <div className="flex-1">
                <p className="font-medium">{g.name} <span className="text-xs text-white/40">· {g.type === "radio" ? "Choix unique" : "Choix multiple"}{g.required ? " · obligatoire" : ""}</span></p>
                <p className="text-xs text-white/50">{g.options.map((o) => `${o.name}${o.price ? ` (+${CHF(o.price)})` : ""}`).join(" · ")}</p>
              </div>
              <Button onClick={() => openApply(g)} variant="outline" className="rounded-none border-brand-amber/50 text-brand-amber bg-transparent hover:bg-brand-amber/10 h-8" data-testid={`apply-group-${g.id}`}>Appliquer</Button>
              <Button onClick={() => openEdit(g)} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10 h-8 w-8 p-0" data-testid={`edit-group-${g.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button onClick={() => del(g)} variant="outline" className="rounded-none border-red-500/40 text-red-400 bg-transparent hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`del-group-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {groups.length === 0 && <p className="text-sm text-white/40 border border-white/10 p-6 text-center">Aucun groupe d'addons. Créez-en un pour commencer.</p>}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col rounded-none border-2 border-white bg-neutral-900 text-white p-0">
          <DialogHeader className="p-5 border-b border-white/15"><DialogTitle className="font-display text-2xl">{editing === "new" ? "Nouveau groupe" : "Modifier le groupe"}</DialogTitle></DialogHeader>
          <div className="p-5 space-y-4 overflow-y-auto">
            <div><Label>Nom du groupe</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Choix du riz" className="rounded-none bg-neutral-800 border-white/20" data-testid="ag-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-none bg-neutral-800 border border-white/20 px-2 py-2 text-sm" data-testid="ag-type">
                  <option value="radio">Choix unique (radio)</option>
                  <option value="checkbox">Choix multiple (checkbox)</option>
                </select>
              </div>
              <div><Label>Max (0 = illimité)</Label><Input type="number" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="ag-max" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="ag-required" />Sélection obligatoire</label>
            <div className="border-t border-white/15 pt-3 space-y-2">
              <Label>Options</Label>
              {form.options.map((o, oi) => (
                <div key={oi} className="flex gap-2 items-center">
                  <Input placeholder="Nom (ex. Riz cantonais)" value={o.name} onChange={(e) => updOpt(oi, { name: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-9" data-testid={`ag-opt-name-${oi}`} />
                  <Input type="number" step="0.5" placeholder="+CHF" value={o.price} onChange={(e) => updOpt(oi, { price: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-9 w-24" data-testid={`ag-opt-price-${oi}`} />
                  <button onClick={() => delOpt(oi)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <Button onClick={addOpt} variant="outline" className="rounded-none border-white/20 bg-transparent h-8 text-xs w-full" data-testid="ag-add-opt-btn">+ Option</Button>
            </div>
          </div>
          <div className="p-5 border-t border-white/15 flex gap-2">
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-none border-white/30 bg-transparent flex-1">Annuler</Button>
            <Button onClick={save} className="rounded-none bg-brand-red hover:bg-brand-red/90 flex-1" data-testid="save-addon-group-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apply dialog */}
      <Dialog open={!!applyGroup} onOpenChange={(o) => !o && setApplyGroup(null)}>
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col rounded-none border-2 border-white bg-neutral-900 text-white p-0">
          <DialogHeader className="p-5 border-b border-white/15"><DialogTitle className="font-display text-xl">Appliquer « {applyGroup?.name} »</DialogTitle></DialogHeader>
          <div className="p-5 space-y-3 overflow-y-auto">
            <label className="flex items-center gap-2 text-sm border border-white/15 p-2"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="apply-replace" />Remplacer tous les addons existants des produits (sinon, ajouter)</label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/60">{targets.length} sélectionné(s)</span>
              <button onClick={() => setTargets(targets.length === products.length ? [] : products.map((p) => p.id))} className="text-xs text-brand-amber" data-testid="apply-select-all">{targets.length === products.length ? "Tout désélectionner" : "Tout sélectionner"}</button>
            </div>
            {cats.map((c) => (
              <div key={c}>
                <p className="text-xs uppercase tracking-widest text-white/40 mt-2 mb-1">{c}</p>
                {products.filter((p) => p.category === c).map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm py-1" data-testid={`apply-target-${p.id}`}>
                    <input type="checkbox" checked={targets.includes(p.id)} onChange={() => toggleTarget(p.id)} />
                    {p.name}
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div className="p-5 border-t border-white/15 flex gap-2">
            <Button onClick={() => setApplyGroup(null)} variant="outline" className="rounded-none border-white/30 bg-transparent flex-1">Annuler</Button>
            <Button onClick={doApply} className="rounded-none bg-brand-red hover:bg-brand-red/90 flex-1" data-testid="do-apply-btn">Appliquer ({targets.length})</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
